import { browser } from "$app/environment";
import { resolve } from "$app/paths";
import { intents, isIntent, type Intent } from "$lib/intents";
import { extraProductInfo, type ProductName } from "$lib/products";
import { getSubscriber, type User } from "$lib/subscription-service";
import type { Reason } from "$lib/types";
import { loadUser, unloadUser } from "$lib/utils";
import { redirect } from "@sveltejs/kit";
import type { LayoutLoad } from "./$types";

/**
 * Set page configuration options
 */
export const prerender = true;
export const trailingSlash = "always";
export const ssr = true;

/**
 * Store receipt in localStorage for receipt linking
 */
// TODO: consider making this dynamic using products.filter(p=>p.localStorageKeyPrefix)
if (browser) {
  // VPN
  const vpnReceiptBlob = sessionStorage.getItem("braveVpn.receipt");
  if (vpnReceiptBlob) {
    localStorage.setItem("braveVpn.receipt", vpnReceiptBlob);
  }

  // Leo
  const leoReceipt = sessionStorage.getItem("braveLeo.receipt");
  if (leoReceipt) {
    localStorage.setItem("braveLeo.receipt", leoReceipt);
  }

  const leoOrderId = sessionStorage.getItem("braveLeo.orderId");
  if (leoOrderId) {
    localStorage.setItem("braveLeo.orderId", leoOrderId);
  }

  // Origin
  const originReceipt = sessionStorage.getItem("braveOrigin.receipt");
  if (originReceipt) {
    localStorage.setItem("braveOrigin.receipt", originReceipt);
  }

  const originOrderId = sessionStorage.getItem("braveOrigin.orderId");
  if (originOrderId) {
    localStorage.setItem("braveOrigin.orderId", originOrderId);
  }
}

const modifiedBase64UrlPattern = /^[A-Za-z0-9_-]*=*?$/;
const permissiveUuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const productNames = Object.keys(extraProductInfo);

/**
 * Load global data for pages
 */
export const load: LayoutLoad = async ({ url }) => {
  if (!browser) return;

  const orderId = url.searchParams.get("order_id");
  const intent = url.searchParams.get("intent") as Intent;
  const reason = url.searchParams.get("reason") as Reason;
  const productName = url.searchParams.get("product") as ProductName;
  const productId = url.searchParams.get("product_id");
  const requestId = url.searchParams.get("requestId");
  const code = url.searchParams.get("code");
  const previousAction = url.searchParams.get("previous_action");

  // Validate query param values
  try {
    if (orderId && !permissiveUuidPattern.test(orderId)) {
      throw new Error("invalid value");
    }
    if (intent && !isIntent(intent, intents)) {
      throw new Error("invalid value");
    }
    if (productName && !productNames.includes(productName)) {
      throw new Error("invalid value");
    }
    if (productId && !permissiveUuidPattern.test(productId)) {
      throw new Error("invalid value");
    }
    if (requestId && !permissiveUuidPattern.test(requestId)) {
      throw new Error("invalid value");
    }
    // The code param can be either a UUID, or a base64 encoded string in the case
    // of accounts verification on /verify
    if (code && !(permissiveUuidPattern.test(code) || modifiedBase64UrlPattern.test(code))) {
      throw new Error("invalid value");
    }
  } catch {
    // In the event of invalid values, strip all query params
    redirect(307, url.pathname);
  }

  /**
   * Intentionally not using the load function's url param in order to 'refetch'
   * from URL every time. Due (ostensibly) to the beforeNavigation handling
   * in +layout.svelte which mutates the to.url.search property, the load function's
   * url param seems to be stale after the mutation.
   */
  const uxMode: "mobile" | any = new URLSearchParams(location.search).get("ux") ?? undefined;

  /**
   * We can safely assume that the user is confirming the log-in action if
   * A. the pathname is the root URL,
   * B. and the URL has both the requestId and code query params.
   */
  const isConfirmingLogin = url.pathname === resolve('/') && requestId && code;

  let user: User;
  try {
    /**
     * We don't want to bother checking for a subscriber if it's clear
     * that the user is attempting to confirm their log-in action via
     * the email roundtrip.
     */
    if (!isConfirmingLogin) {
      user = await getSubscriber();
      loadUser(user);
    }
  } catch (_) {
    unloadUser();
  }

  return {
    orderId,
    intent,
    reason,
    productName,
    productId,
    user,
    uxMode,
    requestId,
    code,
    previousAction,
  };
};
